import {
  ArrowLeft,
  ArrowUpRight,
  PenTool,
  Square,
  Minus,
  Redo2,
  Undo2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ANNOTATION_PALETTE } from "@/lib/annotation-palette";
import { useAnnotation, type AnnotationTool } from "./annotation-context";

// 4 tools — pin was removed 2026-07-11. Anchor-pin comments have their own
// flow triggered from the anchor pin button in the composer / player controls.
const TOOLS: { key: AnnotationTool; label: string; Icon: typeof ArrowUpRight }[] = [
  { key: "arrow", label: "Arrow", Icon: ArrowUpRight },
  { key: "line",  label: "Line",  Icon: Minus },
  { key: "box",   label: "Box",   Icon: Square },
  { key: "draw",  label: "Pen",   Icon: PenTool },
];

/**
 * Annotation toolbar (Task 12). Shown INSIDE the composer's secondary row when
 * annotation mode is active. Replaces the placeholder tool strip during the
 * annotation flow.
 *
 * Layout: 5 shape tools · 5-color palette · undo/redo · cancel-mode.
 */
export function AnnotationToolbar() {
  const { tool, setTool, color, setColor, undo, redo, canUndo, canRedo, deactivate } =
    useAnnotation();

  return (
    <div className="flex flex-wrap items-center gap-2 px-2 pb-2">
      {/* Back — closes the annotation TOOLBAR only. Shapes stay attached to
          the composer draft so the user can type their comment and post. To
          discard shapes, use the composer's "Clear annotation" affordance. */}
      <button
        type="button"
        onClick={deactivate}
        title="Back"
        aria-label="Back"
        className="grid h-7 w-7 place-items-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
      </button>

      {/* Shape tools */}
      <div className="flex items-center gap-0.5">
        {TOOLS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTool(key)}
            title={label}
            aria-label={label}
            aria-pressed={tool === key}
            className={cn(
              "grid h-7 w-7 place-items-center rounded transition-colors",
              tool === key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>

      {/* Color palette */}
      <div className="flex items-center gap-1">
        {ANNOTATION_PALETTE.map((c) => (
          <button
            key={c.hex}
            type="button"
            onClick={() => setColor(c.hex)}
            title={c.name}
            aria-label={`${c.name} color`}
            aria-pressed={color === c.hex}
            className={cn(
              "h-5 w-5 rounded-full border transition-transform",
              color === c.hex ? "border-foreground scale-110" : "border-border",
            )}
            style={{ backgroundColor: c.hex }}
          />
        ))}
      </div>

      {/* Undo / Redo */}
      <div className="ml-auto flex items-center gap-0.5">
        <button
          type="button"
          onClick={undo}
          disabled={!canUndo}
          title="Undo"
          aria-label="Undo"
          className="grid h-7 w-7 place-items-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-40"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={!canRedo}
          title="Redo"
          aria-label="Redo"
          className="grid h-7 w-7 place-items-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-40"
        >
          <Redo2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
